"use client";
import { Globe, Menu, X } from "lucide-react";
import Image from "next/image";
import { Link } from "@/i18n/navigation";
import React, {
  Fragment,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import useTracking from "@/app/common/utils/useTracking";
import { PAGES_LINKS } from "@/app/utils/constants/constants";
import { useRouter } from "@/i18n/navigation";
import ButtonSignIn from "./Loader/ButtonSignIn";

import { getFiatCurrencies } from "@/app/consts/Currencies";
import { CurrencyContext } from "@/app/context/Search/CurrencyContext";
import { useWalletService } from "@/app/context/walletServiceContext/walletServiceContext";
import { CurrencyModal } from "./CurrencyModal";
import CurrencyImage from "./CurrencyModal/CurrencyImage";
import { LangSwitcher } from "./LangSwitcher/LangSwitcher";
import { useTranslations } from "next-intl";
import { useOnClickOutside } from "@/app/hooks/useOnClickOutside";
import { useTenantBranding } from "@/app/context/TenantBrandingContext";
import { AUTH_MODE } from "@/app/enum/wallet";
import ConnectDialog from "../pages/wallet/ConnectDialog";

function HeroHeader() {
  const t = useTranslations();
  const { name: tenantName, logoUrl } = useTenantBranding();
  const { push } = useRouter();
  const [toggle, setToggle] = useState(false);
  const [languageDropdown, setLanguageDropdown] = useState(false);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [selectedCurrencyIcon, setSelectedCurrencyIcon] = useState<ReactNode>();
  const [currencyName, setCurrencyName] = useState("$ - USD");
  const { userDetails, loader, authMode, setOpenModal, openModal } =
    useWalletService();

  const toggleRef = useOnClickOutside(() => setToggle(false));
  const languageDropdownRef = useOnClickOutside(() =>
    setLanguageDropdown(false)
  );

  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const { walletId, points } = userDetails;

  const { currencies, currency, setUpdateCurrency, rate } =
    useContext(CurrencyContext);

  useTracking();

  const handleSignIn = () => {
    if (walletId) {
      push("/account");
    }

    if (authMode == AUTH_MODE.Custom) {
      console.log("Setting the modal: ", authMode);
      setOpenModal(true);
    }
  };

  useEffect(() => {
    if (!currencies.length) return;

    const savedCurrency = currencies.find(
      (item) => item.value === currency.value
    );

    if (savedCurrency) {
      if (savedCurrency.type === "FIAT") {
        const fiatCurrency = getFiatCurrencies(t)?.find(
          (c) => c?.label === savedCurrency?.value
        );

        if (fiatCurrency) {
          setSelectedCurrencyIcon(fiatCurrency?.icon);
          setCurrencyName(fiatCurrency?.name);
        } else {
          setCurrencyName(`${currency?.label}`);
        }
      } else {
        setSelectedCurrencyIcon(
          <CurrencyImage symbol={savedCurrency?.value} />
        );
        setCurrencyName(`${currency?.label}`);
      }
    } else {
      setSelectedCurrencyIcon(
        <Image
          src="/assets/v2/icons/flags/us.svg"
          height={20}
          alt="icon"
          width={20}
        />
      );
    }
  }, [currencies.length, rate]);

  return (
    <>
      <div className="pt-3.5 pb-12 md:pb-32">
        <div className="flex mx-auto items-center gap-x-2">
          <div className="flex items-center flex-auto">
            <div className="flex gap-1 md:gap-2">
              <a href="/">
                {logoUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={logoUrl}
                    className="h-14 w-auto max-w-[240px] object-contain"
                    alt={tenantName}
                  />
                ) : (
                  <span className="text-white font-switzer font-bold text-xl leading-10">{tenantName}</span>
                )}
              </a>
            </div>
          </div>
          <div className=" items-center gap-2 md:flex hidden">
            {!walletId ? (
              <ButtonSignIn
                walletId={walletId}
                handleSignIn={handleSignIn}
                loader={loader}
                points={points}
                authMode={authMode as AUTH_MODE}
              />
            ) : (
              <Link
                className="px-3 py-2 text-base font-semibold bg-white text-sleap-gray-950 rounded-full font-switzer"
                href={PAGES_LINKS.ACCOUNT}
                target="_self"
              >
                {t("Other.yourAccount")}
              </Link>
            )}
            {/* {!walletId ? (
              <button className="px-3 py-2 text-base font-semibold bg-white text-sleap-gray-950 font-switzer">
                {t("Other.signInWithGate")}
              </button>
            ) : (
              ""
            )} */}
            <div className="w-[1px] h-[21px] bg-sleap-white-100"></div>
            <div //@ts-ignore
              ref={languageDropdownRef}
              onClick={() => {
                setToggle(false);
                setLanguageDropdown(!languageDropdown);
              }}
              className="relative p-2 cursor-pointer"
            >
              {languageDropdown ? (
                <X className="text-white cursor-pointer h-5 w-5" />
              ) : (
                <Globe className="h-5 w-5" color="#fff" />
              )}

              <div
                className={` ${
                  languageDropdown ? "block" : "hidden"
                } absolute top-9 right-0 bg-white dark:bg-sleap-gray-950 w-[196px] border-2 border-sleap-off-white dark:border-sleap-gray-850 rounded-xl p-4 z-50`}
              >
                <ul className="dark:text-white text-sleap-gray-800 flex flex-col gap-2 justify-center mb-0 text-base font-medium">
                  <li
                    className="md:block hidden"
                    onClick={() => setLanguageDropdown(false)}
                  >
                    <LangSwitcher />
                  </li>
                </ul>
              </div>
            </div>{" "}
          </div>
          <div>
            <div //@ts-ignore
              ref={toggleRef}
              onClick={() => {
                setLanguageDropdown(false);
                setToggle(!toggle);
              }}
              className="relative"
            >
              {toggle ? (
                <X className="text-white cursor-pointer" />
              ) : (
                <Menu className="text-white cursor-pointer" />
              )}

              <div
                className={` ${
                  toggle ? "block" : "hidden"
                } absolute top-9 right-0 bg-white dark:bg-sleap-gray-950 w-[196px] border-2 border-sleap-off-white dark:border-sleap-gray-850 rounded-xl p-4 z-50`}
              >
                <ul className="dark:text-white text-sleap-gray-800 flex flex-col gap-2 justify-center mb-0 text-base font-medium">
                  <li onClick={() => setToggle(false)}>
                    {currencies.length ? (
                      <span
                        className="cursor-pointer flex dark:text-white text-sleap-gray-800 items-center gap-x-3 p-2"
                        onClick={() => setShowCurrencyModal(true)}
                      >
                        <span className="w-5 h-5">{selectedCurrencyIcon}</span>{" "}
                        {currencyName}
                      </span>
                    ) : (
                      ""
                    )}
                  </li>
                  <li className="block lg:hidden">
                    <hr className="h-[1px] bg-gray-100 dark:bg-sleap-gray-700 border-0" />
                  </li>
                  <li
                    className="md:hidden block"
                    onClick={() => setToggle(false)}
                  >
                    <LangSwitcher />
                  </li>
                  <li className="md:hidden block">
                    <hr className="h-[1px] bg-gray-100 dark:bg-sleap-gray-700 border-0" />
                  </li>

                  <li
                    className="md:hidden block"
                    onClick={() => setToggle(false)}
                  >
                    <Fragment>
                      {!walletId ? (
                        <ButtonSignIn
                          walletId={walletId}
                          handleSignIn={handleSignIn}
                          loader={loader}
                          points={points}
                          authMode={authMode as AUTH_MODE}
                        />
                      ) : (
                        <Link
                          className="font-switzer text-base font-semibold dark:text-white text-sleap-gray-800 p-2"
                          href={PAGES_LINKS.ACCOUNT}
                          target="_self"
                        >
                          {t("Other.yourAccount")}
                        </Link>
                      )}
                      {/* {!walletId ? (
                        <button className="px-0 ps-2 py-2 text-base font-semibold bg-white text-sleap-gray-950">
                          {t("Other.signInWithGate")}
                        </button>
                      ) : (
                        ""
                      )} */}
                    </Fragment>
                  </li>
                  <li className="md:hidden block">
                    <hr className="h-[1px] bg-gray-100 dark:bg-sleap-gray-700 border-0" />
                  </li>
                  <li onClick={() => setToggle(false)}>
                    <Link
                      className="font-switzer text-base font-semibold dark:text-white text-sleap-gray-800 p-2"
                      href={PAGES_LINKS.CONTACT}
                      target="_self"
                    >
                      {t("Other.contact")}
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* <div className="md:flex items-center gap-3 md:gap-4 hidden">
            {currencies.length ? (
              <span
                className="cursor-pointer hidden lg:flex text-white items-center rounded-full gap-x-1 px-4 py-3 text-sm font-semibold bg-[rgba(255,255,255,0.35)] hover:bg-[rgba(255,255,255,0.65)] transition dark:bg-[rgba(139,94,247,0.66)] dark:hover:bg-[rgba(139,94,247,0.76)]"
                onClick={() => setShowCurrencyModal(true)}
              >
                {currencyName}
              </span>
            ) : (
              ""
            )}
          </div> */}
        </div>
      </div>

      {showCurrencyModal && (
        <CurrencyModal
          setOpenModal={setShowCurrencyModal}
          openModal={showCurrencyModal}
          currency={currency}
          setUpdateCurrency={setUpdateCurrency}
          currencies={currencies}
        />
      )}

      {openModal && <ConnectDialog setOpen={setOpenModal} />}
    </>
  );
}

export default HeroHeader;

